import { useMemo } from "react";
import { parseISODate, toISODate } from "@/components/base/DatePicker";

const MONTHS_DE = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];
const MONTHS_EN = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const WEEKDAYS_DE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const WEEKDAYS_EN = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface Cell {
  day: number | null;
  iso: string | null;
  outOfRange: boolean;
  unavailable: boolean;
  isStart: boolean;
  isEnd: boolean;
  inRange: boolean;
  isTurnover: boolean;
}

const emptyCell = (): Cell => ({
  day: null,
  iso: null,
  outOfRange: false,
  unavailable: false,
  isStart: false,
  isEnd: false,
  inRange: false,
  isTurnover: false,
});

interface MonthGridProps {
  year: number;
  month: number;
  unavailable: Set<string>;
  turnover: Set<string>;
  turnoverTooltip?: string;
  checkIn: string;
  checkOut: string;
  hover: string | null;
  minDate: Date | null;
  maxDate: Date | null;
  isDe: boolean;
  onSelect: (iso: string) => void;
  onHover: (iso: string | null) => void;
}

function MonthGrid({
  year,
  month,
  unavailable,
  turnover,
  turnoverTooltip,
  checkIn,
  checkOut,
  hover,
  minDate,
  maxDate,
  isDe,
  onSelect,
  onHover,
}: MonthGridProps) {
  const months = isDe ? MONTHS_DE : MONTHS_EN;
  const weekdays = isDe ? WEEKDAYS_DE : WEEKDAYS_EN;

  const startD = parseISODate(checkIn);
  const endD = parseISODate(checkOut);
  const hoverD = parseISODate(hover ?? "");
  const rangeEnd = endD ?? hoverD;

  const cells = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const offset = (firstDay + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const result: Cell[] = [];

    for (let i = 0; i < offset; i++) result.push(emptyCell());

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const iso = toISODate(date);
      const beforeMin = minDate ? date < minDate : false;
      const afterMax = maxDate ? date > maxDate : false;
      const isStart = iso === checkIn;
      const isEnd = iso === checkOut;
      const inRange =
        !!startD && !!rangeEnd && date > startD && date < rangeEnd && !isStart && !isEnd;
      result.push({
        day: d,
        iso,
        outOfRange: beforeMin || afterMax,
        unavailable: unavailable.has(iso),
        isStart,
        isEnd,
        inRange,
        isTurnover: turnover.has(iso),
      });
    }

    while (result.length % 7 !== 0) result.push(emptyCell());
    return result;
  }, [year, month, unavailable, turnover, checkIn, checkOut, startD, rangeEnd, minDate, maxDate]);

  return (
    <div className="flex-1 min-w-[264px]">
      <div className="text-center font-heading text-foreground-950 mb-3">
        {months[month]} {year}
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {weekdays.map((w) => (
          <div key={w} className="text-center text-xs font-medium text-foreground-600 py-1">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (c.day === null) {
            return <div key={i} className="aspect-square"></div>;
          }
          const disabled = c.outOfRange || c.unavailable;
          const selected = c.isStart || c.isEnd;
          return (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => c.iso && onSelect(c.iso)}
              onMouseEnter={() => {
                if (!disabled) onHover(c.iso);
              }}
              onMouseLeave={() => onHover(null)}
              title={c.isTurnover ? turnoverTooltip : undefined}
              className={`aspect-square rounded-md text-sm font-medium transition-colors relative ${
                c.unavailable
                  ? "bg-red-100/50 text-red-700 cursor-not-allowed"
                  : c.outOfRange
                  ? "text-foreground-300 cursor-not-allowed"
                  : selected
                  ? "bg-primary-600 text-background-50 cursor-pointer"
                  : c.inRange
                  ? "bg-primary-100 text-primary-900 cursor-pointer"
                  : c.isTurnover
                  ? "border border-accent-700 text-foreground-900 cursor-pointer hover:bg-accent-50"
                  : "hover:bg-accent-100 text-foreground-800 cursor-pointer"
              }`}
            >
              {c.day}
              {c.isTurnover && !c.unavailable && (
                <span
                  className={`absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full ${
                    selected ? "bg-background-50" : "bg-accent-500"
                  }`}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface AvailabilityCalendarProps {
  year: number;
  month: number;
  unavailable: Set<string>;
  turnover: Set<string>;
  turnoverTooltip?: string;
  checkIn: string;
  checkOut: string;
  hover: string | null;
  minDate: Date | null;
  maxDate: Date | null;
  isDe: boolean;
  onSelect: (iso: string) => void;
  onHover: (iso: string | null) => void;
  onPrev: () => void;
  onNext: () => void;
}

export default function AvailabilityCalendar({
  year,
  month,
  unavailable,
  turnover,
  turnoverTooltip,
  checkIn,
  checkOut,
  hover,
  minDate,
  maxDate,
  isDe,
  onSelect,
  onHover,
  onPrev,
  onNext,
}: AvailabilityCalendarProps) {
  const right = new Date(year, month + 1, 1);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={onPrev}
          className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 hover:bg-background-200 cursor-pointer text-foreground-800"
          aria-label={isDe ? "Vorheriger Monat" : "Previous month"}
        >
          <i className="ri-arrow-left-s-line"></i>
        </button>
        <div className="text-xs text-foreground-500 px-2 text-center">
          {isDe ? "An- und Abreise wählen" : "Select check-in and check-out"}
        </div>
        <button
          type="button"
          onClick={onNext}
          className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 hover:bg-background-200 cursor-pointer text-foreground-800"
          aria-label={isDe ? "Nächster Monat" : "Next month"}
        >
          <i className="ri-arrow-right-s-line"></i>
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        <MonthGrid
          year={year}
          month={month}
          unavailable={unavailable}
          turnover={turnover}
          turnoverTooltip={turnoverTooltip}
          checkIn={checkIn}
          checkOut={checkOut}
          hover={hover}
          minDate={minDate}
          maxDate={maxDate}
          isDe={isDe}
          onSelect={onSelect}
          onHover={onHover}
        />
        <MonthGrid
          year={right.getFullYear()}
          month={right.getMonth()}
          unavailable={unavailable}
          turnover={turnover}
          turnoverTooltip={turnoverTooltip}
          checkIn={checkIn}
          checkOut={checkOut}
          hover={hover}
          minDate={minDate}
          maxDate={maxDate}
          isDe={isDe}
          onSelect={onSelect}
          onHover={onHover}
        />
      </div>
    </div>
  );
}